import { useEffect, useRef, useState } from "react";
import { useTranslate } from "ra-core";
import { useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import {
  api,
  getMe,
  rememberWorkspace,
  signIn,
  signOut,
} from "../providers/api/client";
import { CenteredCard } from "./CenteredCard";

// The token waits here (this tab only) while the browser goes through sign-in. The link is a hash
// route (/#/invite?token=…), so the token sits in the URL fragment, which no server ever sees; it
// is removed from the address bar at once and never sent through a sign-in redirect.
const TOKEN_KEY = "crm.invite.token";
const REAUTH_KEY = "crm.invite.reauth";

const session = {
  get: (key: string) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Without storage the token only lives in this page load.
    }
  },
  remove: (key: string) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Nothing stored.
    }
  },
};

const status = (error: unknown) => (error as { status?: number }).status;

// Where sign-in brings us back to: this page, without the token.
const RETURN_TO = "/#/invite";

/** /#/invite?token=… : sign in if needed, then join the workspace (ADR-0007 §4). */
export const InvitePage = () => {
  const translate = useTranslate();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const fromLink = new URLSearchParams(location.search).get("token");
    if (fromLink) {
      session.set(TOKEN_KEY, fromLink);
      navigate(InvitePage.path, { replace: true });
    }
    const token = fromLink ?? session.get(TOKEN_KEY);
    if (!token) {
      setError(
        translate("crm.workspaces.invite.incomplete", {
          _: "This invitation link is incomplete. Ask for a new one.",
        }),
      );
      return;
    }

    (async () => {
      try {
        await getMe();
      } catch (failure) {
        if (status(failure) === 401) return signIn(RETURN_TO);
        throw failure;
      }
      try {
        const { data } = await api<{ data: { workspace_id: string } }>(
          "/invitations/accept",
          { method: "POST", body: { token } },
        );
        session.remove(TOKEN_KEY);
        session.remove(REAUTH_KEY);
        rememberWorkspace(data.workspace_id);
        window.location.assign("/");
      } catch (failure) {
        const details = (
          failure as { body?: { details?: { reason?: string } } }
        ).body?.details;
        // Accepting wants a recent sign-in and a verified email, both as the provider says at
        // sign-in. One silent round trip through the provider refreshes them (once).
        if (
          (details?.reason === "reauthenticate" ||
            details?.reason === "unverified") &&
          !session.get(REAUTH_KEY)
        ) {
          session.set(REAUTH_KEY, "1");
          return signIn(RETURN_TO);
        }
        session.remove(REAUTH_KEY);
        if (status(failure) === 404) session.remove(TOKEN_KEY);
        setError((failure as Error).message);
      }
    })().catch((failure: Error) => setError(failure.message));
  }, [translate, location.search, navigate]);

  return (
    <CenteredCard
      title={translate("crm.workspaces.invite.title", {
        _: "Joining a workspace",
      })}
    >
      {error ? (
        <>
          <p className="text-sm text-destructive">{error}</p>
          <div className="flex gap-2">
            {session.get(TOKEN_KEY) ? (
              <Button
                className="flex-1"
                onClick={() => {
                  session.remove(REAUTH_KEY);
                  signIn(RETURN_TO);
                }}
              >
                {translate("crm.workspaces.invite.retry", { _: "Try again" })}
              </Button>
            ) : null}
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => window.location.assign("/")}
            >
              {translate("crm.workspaces.invite.home", { _: "Go to the app" })}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={async () => window.location.assign(await signOut())}
            >
              {translate("ra.auth.logout", { _: "Log out" })}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Spinner />
          {translate("crm.workspaces.invite.working", {
            _: "Checking your invitation…",
          })}
        </div>
      )}
    </CenteredCard>
  );
};

InvitePage.path = "/invite";
